import { AxiosResponse } from "axios";
import { axiosRequest } from "../api.utils";
import { API_ROOT } from "services/baseURLs.config";
import type {
  AddAdditionalRowRequest,
  CompanyWorkerSalary,
  SalaryNotice,
  WorkerSalaryNoticeAdditionalRowResponse,
} from "model/Salary";

const getSalaries = async (
  companyId: number,
  page: number,
  pageSize: number,
  year: number,
  month: number,
): Promise<AxiosResponse<{ salaryNotices: SalaryNotice[]; total: number }>> => {
  return await axiosRequest.auth.get<{
    salaryNotices: SalaryNotice[];
    total: number;
  }>(
    `${API_ROOT}/companies/${companyId}/salaries?page=${page}&pageSize=${pageSize}&year=${year}&month=${month}`,
  );
};

const generateSalaryNotice = async (
  companyId: number,
  workerId: number,
  pdfId: number,
): Promise<AxiosResponse<Blob>> => {
  return await axiosRequest.auth.get<Blob>(
    `${API_ROOT}/companies/${companyId}/salaries/workers/${workerId}/pdf/${pdfId}`,
    {
      headers: {
        "Content-Type": "application/pdf",
      },
      responseType: "blob",
    },
  );
};

const getWorkerSalaries = async (
  companyId: number,
  workerId: number,
  year?: number,
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.get<any>(
    `${API_ROOT}/companies/${companyId}/salaries`,
    {
      params: {
        workerId,
        year,
      },
    },
  );
};

const getAGI = async (
  companyId: number,
  year: number,
  month: number,
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.get<Blob>(
    `${API_ROOT}/companies/${companyId}/salaries/employerPayrollReport/${year}-${month}`,
    {
      headers: {
        "Content-Type": "application/xml",
      },
      responseType: "blob",
    },
  );
};

const getWorkersAGI = async (
  companyId: number,
  year: number,
  month: number,
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.get<any>(
    `${API_ROOT}/companies/${companyId}/salaries/employerPayrollReport/${year}-${month}/workers`,
  );
};

const getCompanyWorkHours = async (
  companyId: number,
  year: number,
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.get<any>(
    `${API_ROOT}/companies/${companyId}/monthlyWorkHours/${year}`,
  );
};

const overrideMonthWorkingHours = async (
  companyId: number,
  period: string,
  hours: number,
): Promise<AxiosResponse> => {
  return await axiosRequest.auth.post(
    `${API_ROOT}/companies/${companyId}/monthlyWorkHours`,

    {
      period,
      hours,
    },
  );
};

const getAllSalaries = (
  companyId: number,
  page: number,
  pageSize: number,
): Promise<AxiosResponse<{ items: CompanyWorkerSalary[]; total: number }>> =>
  axiosRequest.auth.get(
    `${API_ROOT}/companies/${companyId}/workers/v3/salaries?page=${page}&pageSize=${pageSize}`,
  );

const generateHogiaFile = async (
  companyId: number,
  year: number,
  month: number,
): Promise<AxiosResponse<Blob>> => {
  return axiosRequest.auth.post(
    `${API_ROOT}/companies/${companyId}/export/hogia/${year}-${month}`,
    null,
    {
      responseType: "blob",
      headers: {
        Accept: "application/zip",
      },
    },
  );
};

const generateSIEFile = (companyId: number, year: number, month: number) =>
  axiosRequest.auth.post(
    `${API_ROOT}/companies/${companyId}/export/sie4/${year}-${month}`,
    null,
    {
      responseType: "blob",
      headers: { Accept: "text/plain, application/octet-stream, */*" },
    },
  );

const putAdditionalRow = async (
  companyId: number,
  workerId: number,
  request: AddAdditionalRowRequest,
): Promise<AxiosResponse<WorkerSalaryNoticeAdditionalRowResponse[]>> => {
  return await axiosRequest.auth.put<WorkerSalaryNoticeAdditionalRowResponse[]>(
    `${API_ROOT}/companies/${companyId}/salaries/workers/${workerId}/additionalRows`,
    request,
  );
};

const regenerateSalaryNotice = async (
  companyId: number,
  workerId: number,
  salaryNoticeId: number,
): Promise<AxiosResponse<{ value: boolean }>> => {
  return await axiosRequest.auth.put<{ value: boolean }>(
    `${API_ROOT}/companies/${companyId}/salaries/workers/${workerId}/notices/${salaryNoticeId}`,
  );
};

const generateSalaryNotices = async (
  companyId: number,
  year: number,
  month: number,
  workerIds?: number[],
): Promise<AxiosResponse<{ value: boolean }>> => {
  return await axiosRequest.auth.put<{ value: boolean }>(
    `${API_ROOT}/companies/${companyId}/salaries/${year}-${month}`,
    null,
    {
      params: {
        workerIdFilter:
          workerIds !== undefined && workerIds.length > 0
            ? workerIds.join(",")
            : undefined,
      },
    },
  );
};

export const salary = {
  getSalaries,
  generateSalaryNotice,
  getWorkerSalaries,
  getAGI,
  getWorkersAGI,
  getCompanyWorkHours,
  overrideMonthWorkingHours,
  getAllSalaries,
  generateHogiaFile,
  generateSIEFile,
  putAdditionalRow,
  regenerateSalaryNotice,
  generateSalaryNotices,
};
